import type { AxiosInstance } from "axios";

import { validateResponse } from "api/validateResponse";

import { GetActivePatientsResponseSchema, GetEnrolledPatientsResponseSchema } from "../admin/schemas";

import {
  GetTherapistCommissionsResponseSchema,
  GetTherapistEnrollmentsResponseSchema,
  GetTherapistPatientsFunctionalitySchema,
  GetTherapistPatientsPainSchema,
  GetTherapistRatingResponseSchema,
} from "./schemas";
import type { SharedArgs } from "./schemas";
import type { GetTherapistInvitationsArgs, PostTherapistInvitationArgs } from "./schemas/invitations";
import {
  GetTherapistInvitationsResponseSchema,
  PostTherapistInvitationResponseSchema,
  TherapistInvitationSchema,
} from "./schemas/invitations";

interface CreateTherapistServiceArgs {
  transportV1: AxiosInstance;
  transportV2: AxiosInstance;
}

export const createTherapistService = ({ transportV2 }: CreateTherapistServiceArgs) => ({
  async getTherapistActivePatients(args: SharedArgs) {
    const response = await transportV2.get("/therapist/dashboard/active_patients", {
      params: args.params,
    });
    return validateResponse(response, GetActivePatientsResponseSchema, false);
  },

  async getTherapistCommissions(args: SharedArgs) {
    const response = await transportV2.get("/therapist/dashboard/commissions", {
      params: args.params,
    });
    return validateResponse(response, GetTherapistCommissionsResponseSchema, false);
  },

  async getTherapistEnrolledPatients(args: SharedArgs) {
    const response = await transportV2.get("/therapist/dashboard/enrolled_patients", {
      params: args.params,
    });
    return validateResponse(response, GetEnrolledPatientsResponseSchema, false);
  },

  async getTherapistEnrollments() {
    const response = await transportV2.get("/therapist/dashboard/enrollments");
    return validateResponse(response, GetTherapistEnrollmentsResponseSchema, false);
  },

  async getTherapistInvitations(args: GetTherapistInvitationsArgs) {
    const response = await transportV2.get("/therapists/invitations", {
      params: args.params,
    });
    return validateResponse(response, GetTherapistInvitationsResponseSchema, false);
  },

  async getTherapistPatientsFunctionality() {
    const response = await transportV2.get("/therapist/dashboard/functionality");
    return validateResponse(response, GetTherapistPatientsFunctionalitySchema, false);
  },

  async getTherapistPatientsPain() {
    const response = await transportV2.get("/therapist/dashboard/pain");
    return validateResponse(response, GetTherapistPatientsPainSchema, false);
  },

  async getTherapistRating() {
    const response = await transportV2.get("/therapist/dashboard/rating");
    return validateResponse(response, GetTherapistRatingResponseSchema, false);
  },

  async postTherapistInvitation(args: PostTherapistInvitationArgs) {
    const response = await transportV2.post("/therapists/invitations", args.payload);
    return validateResponse(response, PostTherapistInvitationResponseSchema, false);
  },

  async putResendTherapistInvitation(invitationId: number) {
    const response = await transportV2.put(`/therapists/invitations/${invitationId}/resend`);
    return validateResponse(response, TherapistInvitationSchema, false);
  },
});

export type TherapistService = ReturnType<typeof createTherapistService>;
